/* eslint-disable max-depth */
// Modules
import React, { useCallback, useEffect, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { ErrorResponse } from 'apollo-link-error'
import { FormikHelpers } from 'formik'

// Application
import { LoginType, SignInCIPCredentials } from 'typings/auth'
import { Analytics, OutputErrorCode, OutputErrorMsg, ROUTES_PATH, tracking } from 'constants/index'
import { ErrorDetailEnum } from 'typings/login'
import { isEmpty, convertToNumber, getQueryParamsValue, singleSPA } from 'utils'
import { LoginCIPSchema } from 'validators/schema'
import { useLoginHook, useAccountMDMHook, useAnalytics } from 'hooks'
// Components
import { LoginForm, LoginInitial } from '../LoginForm'
import { usePhasedLogin } from 'redesign/hooks/usePhasedLogin'
import { defaultConfig } from 'modules/RemoteConfig/defaultConfig'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'
import { getLoginFailureData } from 'utils/analytics'

export const LoginWrapper: React.VFC = () => {
    const {
        identifier,
        setIdentifier,
        authenticate,
        authenticateCIP,
        getRecaptchaToken,
        isSubmitting,
        setSubmitting,
        authState,
        verifyUser,
    } = useLoginHook()

    const { isFeatureEnabled } = useRemoteConfig()
    const { checkAccountService } = useAccountMDMHook()
    const { phasedLoginVisibility } = usePhasedLogin()
    const history = useHistory()
    const location = useLocation()

    const newRedirectFlow = isFeatureEnabled('AUTH_NEW_REDIRECT_FLOW')
    const submitOnlyLimitedFlow = isFeatureEnabled('AUTH_LIMITED_FLOW_ONLY')

    const laboratory: string | null = getQueryParamsValue(location, 'laboratorio') || null
    const queryParams: string | null = getQueryParamsValue(location, 'origem') || null
    const redirectToParam: string | null = getQueryParamsValue(location, 'redirectTo') || null
    const { trackEvent } = useAnalytics(Analytics.ActivateAccountCipOrphan.LoginAccountNav, queryParams === 'ativacao-cip')

    let redirectLink = `${ROUTES_PATH.FORWARDING_APP}${location.search}`

    if (!newRedirectFlow) {
        const {
            strings: { AUTH_REDIRECT_LOGIN_LIMITED_FLOW },
        } = defaultConfig

        redirectLink = AUTH_REDIRECT_LOGIN_LIMITED_FLOW
    }

    const pushState: Partial<unknown> = {
        useCacheExamsList: false,
    }

    const redirectTo = useMemo(() => {
        if (redirectToParam === null) {
            return
        }

        return redirectToParam[0] === '/' ? redirectToParam : `/${redirectToParam}`
    }, [redirectToParam])

    useEffect(() => {
        if (redirectTo) {
            sessionStorage.setItem('@LiviaAuth:Campaigns', redirectTo)
        } else {
            sessionStorage.removeItem('@LiviaAuth:Campaigns')
        }
    }, [redirectTo])

    const handleTrackEventError = useCallback(
        (codeError: string, message: string, value: string) => {
            trackEvent({
                event: 'log_login_error',
                access: identifier,
                access_type: value.length <= 8 ? 'lab' : 'nav',
                code: codeError,
                message,
                time: new Date().toISOString(),
                fullurl: window.location.href,
            })
        },
        [identifier, trackEvent],
    )

    const handleTrackEventFailureLogin = useCallback(
        (
            analyticsData: {
                loginType?: string
                externalStep?: 'LOGIN' | 'PHASED_LOGIN' | 'PHASED_LOGIN_CADASTRO' | string
                cip?: string
                birthDate?: string
            } = {},
        ) => {
            const event = analyticsData.externalStep as 'LOGIN_FAILURE'
            trackEvent(
                getLoginFailureData({
                    loginType: analyticsData.loginType,
                    externalStepFailure: event,
                }),
            )
        },
        [trackEvent],
    )

    const handleAuthUser = useCallback(
        async ({ cpf, password, birthdate, recaptchaToken }: LoginInitial, userStatus: string) => {
            const payload = {
                username: convertToNumber(cpf),
                password,
                birthdate: identifier === LoginType.cip ? birthdate : undefined,
                recaptchaToken: recaptchaToken || '',
            }

            await authenticate(payload)

            const { isRedirect } = await verifyUser({
                externalStep: phasedLoginVisibility ? 'PHASED_LOGIN' : 'LOGIN',
                loginType: identifier === LoginType.cip ? tracking.LOGIN_TYPE.CIP : tracking.LOGIN_TYPE.CPF,
            })

            if (!isRedirect) {
                setSubmitting(false)

                if (queryParams === 'ativacao-cip') {
                    return history.push(ROUTES_PATH.SIGN_UP_REGISTER_EXAMS_ACTIVATE_NEW_CIP_ORPHAN, {
                        oldToken: authState.token,
                        analytics: Analytics.ActivateAccountCipOrphan.RegisterExamesLogin,
                    })
                }

                singleSPA.historyPush(redirectLink, pushState)
            }
        },
        [
            identifier,
            authenticate,
            verifyUser,
            phasedLoginVisibility,
            setSubmitting,
            queryParams,
            redirectLink,
            pushState,
            history,
            authState.token,
        ],
    )

    const handleAuthUserCip = useCallback(
        async ({ cpf, password, birthdate, recaptchaToken }: LoginInitial, isInvalid: boolean) => {
            const payloadCIP: SignInCIPCredentials = {
                cip: convertToNumber(cpf),
                password,
                dateOfBirth: birthdate,
                lab: laboratory as string,
                recaptchaToken: recaptchaToken || '',
                isInvalid,
            }

            if (laboratory) {
                await authenticateCIP(payloadCIP)
                const { isRedirect } = await verifyUser({
                    externalStep: phasedLoginVisibility ? 'PHASED_LOGIN' : 'LOGIN',
                    loginType: tracking.LOGIN_TYPE.LAB,
                    cip: convertToNumber(cpf),
                    birthDate: birthdate,
                })
                setSubmitting(false)

                if (!isRedirect) {
                    setSubmitting(false)

                    singleSPA.historyPush(redirectLink, {
                        ...pushState,
                        loginType: tracking.LOGIN_TYPE.LAB,
                        cip: payloadCIP.cip,
                    })
                }
                return
            }
            setSubmitting(false)
            history.push(ROUTES_PATH.LOGIN_SELECT_LABORATORY, {
                cip: convertToNumber(cpf),
                password,
                dateOfBirth: birthdate,
                isInvalid,
            })
        },
        [laboratory, setSubmitting, history, authenticateCIP, verifyUser, phasedLoginVisibility, redirectLink, pushState],
    )

    const handleSubmit = async (values: LoginInitial, bag: FormikHelpers<LoginInitial>) => {
        const { cpf, password } = values
        let recaptchaToken = await getRecaptchaToken()
        const errorsLoginType = identifier === LoginType.cip ? { password: ' ', cpf: ' ' } : { cpf: ' ' }
        setSubmitting(true)

        const payload = { ...values, recaptchaToken }
        let userStatus: string = ''
        try {
            // checkAccount
            await LoginCIPSchema.validate(values)

            const { status } = await checkAccountService(cpf)
            userStatus = status as string

            if (userStatus === 'LOGINNOTFOUND' && sessionStorage.getItem('EXTERNAL_COMPANY_DATA')) {
                setSubmitting(false)
                history.push(ROUTES_PATH.SIGN_UP_HOME_REDIRECT)
                return
            }
        } catch (err) {
            const e: ErrorResponse = err

            if (e.graphQLErrors && !isEmpty(e.graphQLErrors)) {
                const codeError = e.graphQLErrors[0].extensions?.code
                handleTrackEventError(codeError, `${e.graphQLErrors[0]?.message} - CheckAccount`, password)
                userStatus = codeError
            }
        }

        try {
            await handleAuthUser(payload, userStatus)
            setSubmitting(false)
            return
        } catch (err) {
            const e: ErrorResponse = err

            if (e.graphQLErrors && !isEmpty(e.graphQLErrors)) {
                const codeError = e.graphQLErrors[0].extensions?.code
                const codeErrorDetailCip = e.graphQLErrors[0].extensions?.errorDetail
                const verifyAuthenticatedCip: ErrorDetailEnum = codeErrorDetailCip?.ErrorDetailEnum?.INVALID_CIP_MDM
                handleTrackEventFailureLogin()

                if (codeError === OutputErrorCode.UNAUTHORIZED) {
                    setSubmitting(false)
                    if (identifier === LoginType.cip) {
                        bag.setErrors({ birthdate: OutputErrorMsg.UNAUTHORIZED, ...errorsLoginType })
                        return
                    }
                    bag.setErrors({ password: OutputErrorMsg.UNAUTHORIZED_CPF_TRY_CIP, ...errorsLoginType })
                    return
                }

                if (codeError === OutputErrorCode.CIPWITHOUTACCOUNT) {
                    recaptchaToken = await getRecaptchaToken()
                    const payloadCIP = { ...values, recaptchaToken }

                    try {
                        await handleAuthUserCip(payloadCIP, !!verifyAuthenticatedCip)
                        return
                    } catch (er) {
                        setSubmitting(false)
                        if (er.graphQLErrors && !isEmpty(er.graphQLErrors)) {
                            const codeErrorCIP = er.graphQLErrors[0].extensions?.code
                            handleTrackEventError(
                                codeErrorCIP || e.networkError?.name || e.response?.toString() || '',
                                `${er.graphQLErrors[0]?.message || e.networkError?.message} - AuthenticateCIP`,
                                password,
                            )
                            if (codeErrorCIP === OutputErrorCode.CIPUNAUTHENTICATED) {
                                bag.setErrors({ birthdate: OutputErrorMsg.UNAUTHORIZED, ...errorsLoginType })
                                return
                            }
                            if (identifier === LoginType.cip) {
                                bag.setErrors({ birthdate: OutputErrorMsg.RECAPTCHATOKENINVALID, ...errorsLoginType })
                                return
                            }
                            bag.setErrors({ password: OutputErrorMsg.RECAPTCHATOKENINVALID, ...errorsLoginType })
                            return
                        }
                        return
                    }
                }
            }
            setSubmitting(false)
            if (identifier === LoginType.cip) {
                bag.setErrors({ birthdate: OutputErrorMsg.RECAPTCHATOKENINVALID, ...errorsLoginType })
                return
            }
            bag.setErrors({ password: OutputErrorMsg.RECAPTCHATOKENINVALID, ...errorsLoginType })
            return
        }
    }

    const handleSubmitLimitedFlow = async (values: LoginInitial, bag: FormikHelpers<LoginInitial>) => {
        const recaptchaToken = await getRecaptchaToken()
        const payloadCIP = { ...values, recaptchaToken }
        const errorsLoginType = { password: ' ', cpf: ' ' }
        try {
            await handleAuthUserCip(payloadCIP, false)
            return
        } catch (er) {
            setSubmitting(false)
            if (er.graphQLErrors && !isEmpty(er.graphQLErrors)) {
                const codeErrorCIP = er.graphQLErrors[0].extensions?.code
                if (codeErrorCIP === OutputErrorCode.CIPUNAUTHENTICATED) {
                    bag.setErrors({ birthdate: OutputErrorMsg.UNAUTHORIZED, ...errorsLoginType })
                    return
                }
                bag.setErrors({ birthdate: OutputErrorMsg.RECAPTCHATOKENINVALID, ...errorsLoginType })
            }
            return
        }
    }

    const initialValues = {
        cpf: '',
        password: '',
        birthdate: '',
        recaptchaToken: '',
    }

    return (
        <LoginForm
            handleSubmit={(values: LoginInitial, bag: FormikHelpers<LoginInitial>) =>
                submitOnlyLimitedFlow ? handleSubmitLimitedFlow(values, bag) : handleSubmit(values, bag)
            }
            identifier={identifier}
            initialValues={initialValues}
            isSubmitting={isSubmitting}
            setIdentifier={setIdentifier}
        />
    )
}
